import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { CalculatorOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { pursuitPath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import {
  buildTenderReadiness,
  readinessTagColor,
  type TenderReadinessState,
} from '@/components/epc/commercial-ux/tenderReadiness';
import { buildTenderIntakeReadiness } from '@/components/epc/commercial-ux/tenderIntakeReadiness';
import { epcInvitationKind, epcInvitationKindLabel } from '@/constants/epcInvitationKind';
import { formatDate } from '@/utils/format';

const { Title, Text } = Typography;

type FilterTab = 'all' | 'READY' | 'BLOCKED' | 'IN_PROGRESS';

const EpcTendersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const estimateIntent = searchParams.get('intent') === 'estimate';
  const [filter, setFilter] = useState<FilterTab>('all');
  const kindParam = searchParams.get('kind');
  const kindFilter: 'all' | 'RFQ' | 'RFT' =
    kindParam === 'RFQ' || kindParam === 'RFT' ? kindParam : 'all';
  const setKindFilter = (value: 'all' | 'RFQ' | 'RFT') => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('kind');
    else next.set('kind', value);
    setSearchParams(next, { replace: true });
  };

  const listQ = useQuery({
    queryKey: ['epc-tenders'],
    queryFn: () => epcCommercialService.listTenders(),
    staleTime: 15_000,
  });

  const rows = (listQ.data || []) as Record<string, unknown>[];

  const enriched = useMemo(
    () =>
      rows.map((r) => ({
        row: r,
        readiness: buildTenderReadiness(r),
        intake: buildTenderIntakeReadiness(r),
      })),
    [rows]
  );

  const filtered = useMemo(() => {
    const byKind = enriched.filter((e) => {
      const kind = epcInvitationKind(e.row.invitation_kind || e.row.invitationKind) || 'RFT';
      if (kindFilter === 'all') return true;
      return kind === kindFilter;
    });
    if (filter === 'all') return byKind;
    return byKind.filter((e) => e.readiness.overall === filter);
  }, [enriched, filter, kindFilter]);

  const counts = useMemo(() => {
    const c = { READY: 0, BLOCKED: 0, IN_PROGRESS: 0 };
    for (const e of enriched) c[e.readiness.overall] += 1;
    return c;
  }, [enriched]);

  const openWorkbench = (tenderId: string, importBoq?: boolean) => {
    const q = new URLSearchParams();
    q.set('tab', 'estimate');
    if (importBoq) q.set('importBoq', '1');
    navigate(`/epc/commercial/tenders/${tenderId}?${q.toString()}`);
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        {estimateIntent
          ? 'Tender Estimation'
          : kindFilter === 'RFQ'
            ? 'Request for Quotation'
            : kindFilter === 'RFT'
              ? 'Request for Tender'
              : 'Tender register'}
      </Title>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Text type="secondary" style={{ display: 'block' }}>
          {estimateIntent
            ? 'Open a tender’s Estimating Workbench to import Client BOQ, price lines, and plug WBS packages.'
            : 'Request for Quotation and Request for Tender are client invitations we respond to. Supplier quotations stay in Procurement.'}
        </Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/epc/commercial/invitations/new?kind=RFT')}
        >
          New invitation
        </Button>
      </Space>

      {estimateIntent ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Client BOQ upload"
          description="Use Import BOQ on a tender row. That opens the Estimating Workbench Client BOQ import wizard."
        />
      ) : null}

      <Tabs
        activeKey={filter}
        onChange={(k) => setFilter(k as FilterTab)}
        items={[
          { key: 'all', label: `All (${enriched.length})` },
          { key: 'READY', label: `Ready (${counts.READY})` },
          { key: 'BLOCKED', label: `Blocked (${counts.BLOCKED})` },
          { key: 'IN_PROGRESS', label: `In progress (${counts.IN_PROGRESS})` },
        ]}
        style={{ marginBottom: 8 }}
      />

      <Space style={{ marginBottom: 8 }}>
        <Select
          value={kindFilter}
          onChange={setKindFilter}
          style={{ width: 220 }}
          options={[
            { value: 'all', label: 'All invitations' },
            { value: 'RFQ', label: 'Request for Quotation' },
            { value: 'RFT', label: 'Request for Tender' },
          ]}
        />
      </Space>
      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.row.id)}
          dataSource={filtered}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
          onRow={(r) => ({
            onClick: () =>
              estimateIntent
                ? openWorkbench(String(r.row.id))
                : navigate(`/epc/commercial/tenders/${r.row.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Tender #', dataIndex: ['row', 'tender_number'], width: 140 },
            {
              title: 'Invitation',
              key: 'invitation',
              width: 180,
              render: (_: unknown, r) =>
                epcInvitationKindLabel(r.row.invitation_kind || r.row.invitationKind || 'RFT'),
            },
            {
              title: 'Client no.',
              dataIndex: ['row', 'client_document_no'],
              width: 140,
              render: (v) => v || '—',
            },
            {
              title: 'Closing',
              dataIndex: ['row', 'closing_at'],
              width: 120,
              render: (v) => (v ? formatDate(String(v)) : '—'),
            },
            {
              title: 'Current',
              key: 'current',
              width: 90,
              render: (_: unknown, r) =>
                r.row.is_current === 0 || r.row.is_current === false ? (
                  '—'
                ) : (
                  <Tag color="blue">Current</Tag>
                ),
            },
            { title: 'Title', dataIndex: ['row', 'title'] },
            {
              title: 'Client',
              dataIndex: ['row', 'client_name'],
              render: (v) => v || '—',
            },
            {
              title: 'Readiness',
              key: 'readiness',
              width: 130,
              render: (_: unknown, r) => (
                <Tag color={readinessTagColor(r.readiness.overall as TenderReadinessState)}>
                  {r.readiness.overall}
                </Tag>
              ),
            },
            {
              title: 'Intake',
              key: 'intake',
              width: 130,
              render: (_: unknown, r) => (
                <Tag color={r.intake.ready ? 'green' : 'orange'}>
                  {r.intake.intakeStatus.replace('_', ' ')}
                </Tag>
              ),
            },
            {
              title: 'Model',
              dataIndex: ['row', 'commercial_model'],
              width: 120,
              render: (v) => v || '—',
            },
            {
              title: 'Bid due',
              dataIndex: ['row', 'bid_due_at'],
              width: 120,
              render: (v) => (v ? formatDate(String(v)) : '—'),
            },
            {
              title: '',
              width: 320,
              render: (_: unknown, r) => {
                const oppId = String(r.row.opportunity_id || r.row.opportunityId || '');
                const tid = String(r.row.id);
                return (
                  <Space size={0} wrap onClick={(e) => e.stopPropagation()}>
                    <Button type="link" onClick={() => navigate(`/epc/commercial/tenders/${tid}`)}>
                      Workspace
                    </Button>
                    <Button
                      type="link"
                      icon={<CalculatorOutlined />}
                      onClick={() => openWorkbench(tid)}
                    >
                      Workbench
                    </Button>
                    <Button
                      type="link"
                      icon={<ImportOutlined />}
                      onClick={() => openWorkbench(tid, true)}
                    >
                      Import BOQ
                    </Button>
                    {oppId ? (
                      <Button type="link" onClick={() => navigate(pursuitPath(oppId))}>
                        Pursuit
                      </Button>
                    ) : null}
                  </Space>
                );
              },
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default EpcTendersPage;
